import React from "react"
import { GroupRow } from "~/api/snapshot_summaries"
import {
  formatCurrency,
  formatCurrencyFull,
  formatPercent,
  formatTvpi,
  formatIrr,
  formatAge,
} from "./utils"
import type { Grouping, View } from "./utils"
import Tooltip from "~/components/Tooltip"

interface TotalsRowProps {
  totals: GroupRow
  grouping: Grouping
  view: View
}

function valueClass(value: number | null | undefined): string {
  if (value == null) return ""
  if (value > 0) return "b-cell--positive"
  if (value < 0) return "b-cell--negative"
  return ""
}

function tvpiClass(tvpi: number | null | undefined): string {
  if (tvpi == null) return ""
  return tvpi >= 1.0 ? "b-cell--positive" : "b-cell--negative"
}

function cTd(value: number | null | undefined, extraClass?: string) {
  return (
    <td className={extraClass}>
      <Tooltip tip={value != null ? formatCurrencyFull(value) : undefined}>
        {formatCurrency(value)}
      </Tooltip>
    </td>
  )
}

function vTd(value: number | null | undefined) {
  return cTd(value, valueClass(value))
}

const TotalsRow = ({ totals, grouping, view }: TotalsRowProps) => {
  const p = totals.performance
  const qa = totals.quarter_activity
  const y = totals.ytd_performance
  const isSeries = grouping === "series"

  return (
    <tr className="b-snapshot-summary-totals-row">
      <td className="b-snapshot-summary-label-cell">Total</td>
      {isSeries ? (
        [0, 1, 2].map((i) => <td key={i}></td>)
      ) : (
        <td>{totals.series_count}</td>
      )}
      {cTd(totals.commitment)}
      {view === "performance" && (
        <>
          <td>{formatPercent(p.called_pct)}</td>
          {cTd(p.called != null ? -p.called : null)}
          {cTd(p.dist)}
          {cTd(p.mv)}
          {vTd(p.pl)}
          <td className={tvpiClass(p.tvpi)}>{formatTvpi(p.tvpi)}</td>
          {isSeries && <td>{formatIrr(p.irr)}</td>}
          <td className={tvpiClass(p.dpi)}>{formatTvpi(p.dpi)}</td>
          {isSeries && <td>{formatAge(p.age)}</td>}
          {vTd(qa.pl)}
          {vTd(qa.net_cf)}
          <td>—</td>
          <td>{formatPercent(p.mv_pct)}</td>
          <td>{formatPercent(p.pl_pct)}</td>
          <td>{formatPercent(qa.qoq_pl_pct)}</td>
        </>
      )}
      {view === "quarter_activity" && (
        <>
          {vTd(qa.net_cf)}
          {vTd(qa.pl)}
          {vTd(qa.delta_mv)}
          <td className={valueClass(qa.delta_mv_pct)}>
            {formatPercent(qa.delta_mv_pct)}
          </td>
          <td>{formatPercent(qa.called_pct)}</td>
          {cTd(qa.called != null ? -qa.called : null)}
          {cTd(qa.dist)}
        </>
      )}
      {view === "ytd" && (
        <>
          <td>{formatPercent(y.called_pct)}</td>
          {cTd(y.called != null ? -y.called : null)}
          {cTd(y.dist)}
          {vTd(y.delta_mv)}
          {vTd(y.net_cf)}
          {vTd(y.pl)}
        </>
      )}
    </tr>
  )
}

export default TotalsRow
